// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms under AGPL-3.0 section 7(b) apply — see ADDITIONAL-TERMS.md.

// The static DOM for the app, plus modal bodies. Kept as strings so main.ts and
// ui.ts stay focused on behaviour.

const logoSvg = `
<svg class="brand-mark" width="26" height="26" viewBox="0 0 512 512" aria-hidden="true">
  <rect width="512" height="512" rx="120" fill="#12211d"/>
  <g stroke-linecap="round" fill="none">
    <g stroke="#39463f" stroke-width="26"><path d="M112 256 v40"/><path d="M156 256 v-64"/></g>
    <path d="M208 256 q28 -108 56 0 q28 108 56 0 q28 -108 56 0" stroke="#34e6bf" stroke-width="30"/>
  </g>
</svg>`;

export const APP_HTML = `
  <header class="topbar">
    <div class="brand">${logoSvg}<span class="brand-name">Hushwell</span></div>
    <nav class="topbar-actions" aria-label="Primary">
      <button type="button" class="link-btn" data-modal="how">How it works</button>
      <button type="button" class="link-btn" data-modal="privacy">Privacy</button>
      <button type="button" class="link-btn" data-modal="about">About</button>
      <button type="button" class="icon-btn" id="log-toggle" aria-expanded="false" aria-label="Event log" title="Event log">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 7h16M4 12h16M4 17h10"/></svg>
      </button>
    </nav>
  </header>

  <main class="main-content">
    <section class="hero" id="hero">
      <h1>Clean the noise out of any voice recording</h1>
      <p class="sub">Hum, hiss, fans, traffic and keyboard clatter removed by an on-device AI model. Drop an audio clip or record yourself — the recording never leaves your browser.</p>
      <button type="button" class="trust-badge" data-modal="privacy">
        <span class="dot"></span> Runs entirely in your browser · nothing is uploaded
      </button>
    </section>

    <section class="stage" id="stage">
      <!-- INPUT -->
      <div class="panel input-panel" id="input-panel">
        <div class="dropzone" id="dropzone" tabindex="0" role="button" aria-label="Choose an audio file">
          <svg class="dz-icon" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 15V3m0 0 4 4m-4-4L8 7"/><path d="M4 15v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4"/></svg>
          <p class="dz-title">Drop an audio file here</p>
          <p class="dz-sub">MP3 · M4A · WAV · AAC · OGG · FLAC — or a video's audio track</p>
          <div class="dz-actions">
            <button type="button" class="btn primary" id="pick-btn">Choose file</button>
            <button type="button" class="btn ghost" id="record-btn">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="12" r="6"/></svg>
              Record
            </button>
          </div>
          <button type="button" class="sample-pill" id="sample-btn">Try a sample clip</button>
          <input type="file" id="file-input" accept="audio/*,video/*,.mp3,.m4a,.wav,.aac,.ogg,.oga,.flac,.opus,.webm,.mp4,.mov" hidden />
        </div>
      </div>

      <!-- RECORDING -->
      <div class="panel record-panel" id="record-panel" hidden>
        <div class="rec-indicator"><span class="rec-dot"></span> Recording</div>
        <div class="level-meter" aria-hidden="true"><span class="level-fill" id="level-fill"></span></div>
        <div class="rec-time" id="rec-time">0:00</div>
        <p class="rec-hint">Speak normally. Nothing is saved until you stop, and nothing is uploaded.</p>
        <div class="rec-actions">
          <button type="button" class="btn ghost" id="rec-cancel">Cancel</button>
          <button type="button" class="btn primary" id="rec-stop">Stop &amp; clean</button>
        </div>
      </div>

      <!-- PROGRESS -->
      <div class="panel progress-panel" id="progress-panel" hidden>
        <div class="progress-head">
          <span id="progress-label">Cleaning…</span>
          <span id="progress-pct" class="mono">0%</span>
        </div>
        <div class="progress-track"><span class="progress-fill" id="progress-fill"></span></div>
        <div class="progress-sub" id="progress-sub">Loading the noise-suppression model…</div>
        <button type="button" class="btn ghost small" id="cancel-btn">Cancel</button>
      </div>

      <!-- RESULT -->
      <div class="panel result-panel" id="result-panel" hidden>
        <div class="result-headline">
          <div class="big-stat"><span id="reduction-num">0</span><span class="unit">dB</span></div>
          <div class="big-stat-label">quieter <span class="glossary-link" data-term="noise floor">background</span><br /><span class="muted" id="floor-detail"></span></div>
        </div>

        <div class="wave-block">
          <div class="wave-row">
            <button type="button" class="play-btn" id="play-dry" aria-label="Play original">
              <svg class="ic-play" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
              <svg class="ic-pause" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" hidden><path d="M7 5h4v14H7zM13 5h4v14h-4z"/></svg>
            </button>
            <div class="wave-wrap"><canvas class="wave" id="wave-dry"></canvas><span class="wave-tag">Original</span></div>
          </div>
          <div class="wave-row">
            <button type="button" class="play-btn accent" id="play-wet" aria-label="Play cleaned">
              <svg class="ic-play" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
              <svg class="ic-pause" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" hidden><path d="M7 5h4v14H7zM13 5h4v14h-4z"/></svg>
            </button>
            <div class="wave-wrap"><canvas class="wave" id="wave-wet"></canvas><span class="wave-tag accent">Cleaned</span></div>
          </div>
        </div>

        <div class="strength">
          <label for="strength-slider">Strength <span class="muted">(<span class="glossary-link" data-term="dry / wet">dry / wet</span>)</span></label>
          <input type="range" id="strength-slider" min="0" max="100" value="100" />
          <output id="strength-val" class="mono">100%</output>
        </div>

        <div class="output-bar">
          <div class="format-toggle" role="group" aria-label="Output format">
            <button type="button" class="fmt active" data-fmt="wav">WAV</button>
            <button type="button" class="fmt" data-fmt="mp3">MP3</button>
          </div>
          <button type="button" class="btn primary" id="download-btn">Download cleaned audio</button>
        </div>
        <div class="secondary-actions">
          <button type="button" class="chip" id="share-btn" hidden>Share</button>
          <button type="button" class="chip" id="report-btn">Save report image</button>
          <button type="button" class="chip" id="copy-btn">Copy summary</button>
          <button type="button" class="chip" id="again-btn">Clean another</button>
        </div>
      </div>

      <div class="error-box" id="error-box" hidden>
        <p id="error-text"></p>
        <button type="button" class="btn ghost small" id="error-retry">Try again</button>
      </div>
    </section>
  </main>

  <footer class="site-footer">
    <div class="footer-inner">
      Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
      · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>
    </div>
  </footer>

  <aside class="event-drawer" id="event-drawer" hidden aria-label="Event log">
    <header class="drawer-head">
      <span>Event log</span>
      <button type="button" class="icon-btn" id="event-close" aria-label="Close event log">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
      </button>
    </header>
    <div class="event-list" id="event-list"></div>
    <p class="drawer-foot">Every line above happened on your device. No audio was sent anywhere.</p>
  </aside>

  <div class="modal-host" id="modal-host" hidden>
    <div class="modal-backdrop" id="modal-backdrop"></div>
    <div class="modal" role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <header class="modal-head">
        <h2 id="modal-title"></h2>
        <button type="button" class="icon-btn" id="modal-close" aria-label="Close">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
        </button>
      </header>
      <div class="modal-body" id="modal-body"></div>
    </div>
  </div>

  <div class="toast" id="toast" hidden></div>
`;

export const MODALS: Record<string, { title: string; body: string }> = {
  how: {
    title: 'How it works',
    body: `
      <ol class="steps">
        <li><strong>You choose a recording.</strong> Drop a file, or record from your mic. It is decoded in this tab and never uploaded.</li>
        <li><strong>We prepare the audio.</strong> The clip is mixed to mono and resampled to 48,000 Hz — the <span class="glossary-link" data-term="sample rate">sample rate</span> the model expects.</li>
        <li><strong>The model cleans it, frame by frame.</strong> <span class="glossary-link" data-term="rnnoise">RNNoise</span> — a small recurrent neural network compiled to <span class="glossary-link" data-term="wasm">WebAssembly</span> — processes every 10&nbsp;ms of audio, keeping the voice and suppressing steady background noise.</li>
        <li><strong>We measure the difference.</strong> Using <span class="glossary-link" data-term="vad">voice-activity detection</span> we estimate the <span class="glossary-link" data-term="noise floor">noise floor</span> before and after, so you get a real "quieter by N dB" number.</li>
        <li><strong>You dial it in and export.</strong> The strength slider blends the <span class="glossary-link" data-term="dry / wet">dry and wet</span> signals live, and you download a clean WAV or MP3.</li>
      </ol>
      <p class="fine">RNNoise is best at steady, broadband noise (fans, hiss, hum, air-con, traffic, room tone). It is tuned for speech — music and non-voice audio may be affected.</p>
    `,
  },
  privacy: {
    title: 'Privacy & threat model',
    body: `
      <h3>Protected</h3>
      <ul>
        <li>Your recording is decoded, denoised and encoded <strong>entirely in this browser tab</strong>. No audio is ever uploaded — there is no server to upload it to.</li>
        <li>The noise-suppression model ships inside the page, so cleaning a clip makes <strong>no network request at all</strong>.</li>
        <li>Once the page has loaded, you can go offline and it keeps working.</li>
      </ul>
      <h3>Not protected</h3>
      <ul>
        <li>Loading the page itself is a normal web request: GitHub Pages (the host) and its CDN see that a browser fetched the app, over TLS — as with visiting any website.</li>
        <li>A <strong>Cloudflare Web Analytics</strong> beacon records an anonymous page view — no cookies, no fingerprinting, no cross-site tracking. Your audio is never sent to it.</li>
        <li>Feedback you choose to send goes to <strong>feedback.benrichardson.dev</strong>. Nothing is sent unless you open the feedback form and press Send; your recordings never are.</li>
      </ul>
      <h3>Trust surface</h3>
      <ul>
        <li>The static site bundle, hash-pinned by the GitHub Pages deploy.</li>
        <li>The TLS chain between you and GitHub Pages.</li>
        <li>The RNNoise WebAssembly module (Apache-2.0, from shiguredo/rnnoise-wasm) running locally.</li>
      </ul>
    `,
  },
  about: {
    title: 'About Hushwell',
    body: `
      <p>Hushwell removes background noise from voice recordings without uploading them anywhere. It's the private answer to the cloud "audio enhancer" tools — same idea, but your recording stays on your device.</p>
      <p>It's built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">Ben Richardson</a> as part of a small lab of privacy-first, client-side tools. See the rest at <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">lab.benrichardson.dev</a>.</p>
      <p>Source and licence: <a href="https://github.com/ben-gy/hushwell" target="_blank" rel="noopener">github.com/ben-gy/hushwell</a>. Denoising by <a href="https://github.com/shiguredo/rnnoise-wasm" target="_blank" rel="noopener">RNNoise (WebAssembly)</a>.</p>
      <p class="fine">No accounts, no cookies, no tracking beyond an anonymous page-view count.</p>
    `,
  },
};
